import { orderXClient } from '@/service/client'
import type { InstrumentTrade } from '@/types/generated/common/trading'
import type {
  DataPayload,
  GetGexAggregateRequest,
  GetGreeksByExpiryRequest,
  GetImpliedMoveRequest,
  GetIvPercentileRequest,
  GetMaxPainRequest,
  GetOptionsOiLeaderboardRequest,
  GetOptionsVolumeLeaderboardRequest,
  GexAggregate,
  GreeksByExpiry,
  ImpliedMove,
  IvPercentile,
  MaxPain,
  OptionsOiLeaderboard,
  OptionsOiSeries,
  OptionsOiSeriesRequest,
  OptionsStrikeFlow,
  OptionsStrikeFlowRequest,
  OptionsTradesHistoryRequest,
  OptionsVolumeLeaderboard,
  OptionsVolumeSeries,
  OptionsVolumeSeriesRequest,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// ENG-613 — read-only options analytics pull endpoints (volume/OI series + leaderboards).

export async function fetchOptionsVolumeSeries(
  params: OptionsVolumeSeriesRequest,
  signal?: AbortSignal
): Promise<OptionsVolumeSeries> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsVolumeSeries: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const series = data.optionsVolumeSeries
    if (!series) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options volume series',
      } satisfies ErrorResponse)
    }
    return series
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchOptionsStrikeFlow(
  params: OptionsStrikeFlowRequest,
  signal?: AbortSignal
): Promise<OptionsStrikeFlow> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsStrikeFlow: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const flow = data.optionsStrikeFlow
    if (!flow) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options strike flow',
      } satisfies ErrorResponse)
    }
    return flow
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchOptionsVolumeLeaderboard(
  params: GetOptionsVolumeLeaderboardRequest,
  signal?: AbortSignal
): Promise<OptionsVolumeLeaderboard> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsVolumeLeaderboard: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const leaderboard = data.optionsVolumeLeaderboard
    if (!leaderboard) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options volume leaderboard',
      } satisfies ErrorResponse)
    }
    return leaderboard
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchOptionsOiSeries(
  params: OptionsOiSeriesRequest,
  signal?: AbortSignal
): Promise<OptionsOiSeries> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsOiSeries: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const series = data.optionsOiSeries
    if (!series) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options open interest series',
      } satisfies ErrorResponse)
    }
    return series
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchOptionsOiLeaderboard(
  params: GetOptionsOiLeaderboardRequest,
  signal?: AbortSignal
): Promise<OptionsOiLeaderboard> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsOiLeaderboard: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const leaderboard = data.optionsOiLeaderboard
    if (!leaderboard) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options open interest leaderboard',
      } satisfies ErrorResponse)
    }
    return leaderboard
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchOptionsTradesHistory(
  params: OptionsTradesHistoryRequest,
  signal?: AbortSignal
): Promise<InstrumentTrade[]> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      {
        optionsTradesHistory: params,
      },
      {
        headers: {
          'X-Decode-Key': 'datapayload',
        },
        signal,
      }
    )

    const list = data.instrumentTrades
    if (!list) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch options trades history',
      } satisfies ErrorResponse)
    }
    return list.instrumentTrades
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

// ENG-738 §7.1 — snapshot-only derived panels (no from/to, no pagination).

export async function fetchImpliedMove(
  params: GetImpliedMoveRequest,
  signal?: AbortSignal
): Promise<ImpliedMove> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { impliedMove: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const impliedMove = data.impliedMove
    if (!impliedMove) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch implied move',
      } satisfies ErrorResponse)
    }
    return impliedMove
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchIvPercentile(
  params: GetIvPercentileRequest,
  signal?: AbortSignal
): Promise<IvPercentile> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { ivPercentile: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const ivPercentile = data.ivPercentile
    if (!ivPercentile) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch IV percentile',
      } satisfies ErrorResponse)
    }
    return ivPercentile
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchMaxPain(
  params: GetMaxPainRequest,
  signal?: AbortSignal
): Promise<MaxPain> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { maxPain: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const maxPain = data.maxPain
    if (!maxPain) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch max pain',
      } satisfies ErrorResponse)
    }
    return maxPain
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

export async function fetchGreeksByExpiry(
  params: GetGreeksByExpiryRequest,
  signal?: AbortSignal
): Promise<GreeksByExpiry> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { greeksByExpiry: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const greeksByExpiry = data.greeksByExpiry
    if (!greeksByExpiry) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch greeks by expiry',
      } satisfies ErrorResponse)
    }
    return greeksByExpiry
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}

/**
 * Dealer gamma rolled up from the latest `gex` snapshot — by strike (summed
 * across expirations), by expiry+strike, by expiry+type, and the spot-vol
 * hedging split. Snapshot-only (no from/to): the backend already does the
 * rollup, so the frontend doesn't need per-instrument rows for this view.
 */
export async function fetchGexAggregate(
  params: GetGexAggregateRequest,
  signal?: AbortSignal
): Promise<GexAggregate> {
  try {
    const { data } = await orderXClient.post<DataPayload>(
      '/api',
      { gexAggregate: params },
      { headers: { 'X-Decode-Key': 'datapayload' }, signal }
    )

    const gexAggregate = data.gexAggregate
    if (!gexAggregate) {
      return Promise.reject({
        status: 400,
        message: 'Failed to fetch GEX aggregate',
      } satisfies ErrorResponse)
    }
    return gexAggregate
  } catch (e) {
    return Promise.reject(e as ErrorResponse)
  }
}
